/**
 * NotificationPreferencesTab — Centralise les préférences d'envoi d'emails
 * pour tous les flux automatisés de l'application (réservations, demandes
 * produit, alertes SADAGAS, rapports hebdo, etc.).
 *
 * Permet à un admin de :
 *   - Activer/désactiver chaque catégorie d'email
 *   - Modifier la liste des destinataires (une adresse par ligne)
 *   - Envoyer un email de test à la liste configurée
 *
 * Sauvegarde dynamique (chaque champ s'enregistre via PUT au blur/toggle).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Mail, Send, Save, Bell, BellOff, RefreshCw, Plus, X,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('dermascan_token')}` });

const CATEGORY_LABELS = {
  client: 'Demandes clients',
  sadagas: 'SADAGAS · Réceptions',
  b2b: 'B2B · Cotations cliniques',
  mediciel: 'Mediciel · Stock',
  rh: 'Performance · RH',
};

const CATEGORY_COLORS = {
  client: 'bg-emerald-50 border-emerald-200',
  sadagas: 'bg-blue-50 border-blue-200',
  b2b: 'bg-cyan-50 border-cyan-200',
  mediciel: 'bg-amber-50 border-amber-200',
  rh: 'bg-violet-50 border-violet-200',
};

const PreferenceCard = ({ pref, onUpdate, onTest, savingKey, testingKey }) => {
  const [enabled, setEnabled] = useState(pref.enabled);
  const [recipients, setRecipients] = useState(pref.recipients || []);
  const [newEmail, setNewEmail] = useState('');
  const isSaving = savingKey === pref.key;
  const isTesting = testingKey === pref.key;

  // Sync when parent reloads
  useEffect(() => {
    setEnabled(pref.enabled);
    setRecipients(pref.recipients || []);
  }, [pref.enabled, pref.recipients]);

  const toggleEnabled = async () => {
    const next = !enabled;
    setEnabled(next);
    await onUpdate(pref.key, { enabled: next });
  };

  const addEmail = async () => {
    const e = newEmail.trim();
    if (!e || !e.includes('@')) {
      toast.error('Email invalide.');
      return;
    }
    if (recipients.includes(e)) {
      toast.info('Email déjà présent.');
      return;
    }
    const next = [...recipients, e];
    setRecipients(next);
    setNewEmail('');
    await onUpdate(pref.key, { recipients: next });
  };

  const removeEmail = async (e) => {
    const next = recipients.filter((x) => x !== e);
    setRecipients(next);
    await onUpdate(pref.key, { recipients: next });
  };

  return (
    <div
      className={`border-2 rounded-lg p-4 ${CATEGORY_COLORS[pref.category] || 'bg-zinc-50 border-zinc-200'}`}
      data-testid={`notif-pref-card-${pref.key}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-zinc-900 text-sm flex items-center gap-1.5">
            {pref.label}
            {isSaving && <Loader2 className="w-3 h-3 animate-spin text-zinc-400" />}
          </p>
          <p className="text-xs text-zinc-600 mt-0.5">{pref.description}</p>
        </div>
        <button
          type="button"
          onClick={toggleEnabled}
          disabled={isSaving}
          className={`shrink-0 relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
            enabled ? 'bg-emerald-500' : 'bg-zinc-300'
          } ${isSaving ? 'opacity-50' : ''}`}
          data-testid={`notif-pref-toggle-${pref.key}`}
          aria-label={enabled ? 'Désactiver' : 'Activer'}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      <div className="mt-3 space-y-1.5">
        <p className="text-[11px] uppercase tracking-wide text-zinc-500 font-medium">
          Destinataires ({recipients.length})
        </p>
        {recipients.length === 0 && (
          <p className="text-xs text-zinc-400 italic">Aucun destinataire — ajoutez une adresse ci-dessous.</p>
        )}
        <div className="flex flex-wrap gap-1.5">
          {recipients.map((e) => (
            <Badge
              key={e}
              variant="secondary"
              className="bg-white border border-zinc-300 text-zinc-800 pl-2 pr-1 py-0.5 flex items-center gap-1"
              data-testid={`notif-pref-email-${pref.key}-${e}`}
            >
              <Mail className="w-3 h-3 text-zinc-400" />
              <span className="text-xs">{e}</span>
              <button
                type="button"
                onClick={() => removeEmail(e)}
                disabled={isSaving}
                className="ml-1 hover:bg-red-50 hover:text-red-600 rounded p-0.5 transition"
                data-testid={`notif-pref-remove-${pref.key}-${e}`}
                aria-label={`Retirer ${e}`}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>

        <div className="flex gap-1.5 mt-2">
          <Input
            type="email"
            placeholder="email@exemple.com"
            value={newEmail}
            onChange={(ev) => setNewEmail(ev.target.value)}
            onKeyDown={(ev) => { if (ev.key === 'Enter') { ev.preventDefault(); addEmail(); } }}
            className="h-8 text-sm flex-1"
            disabled={isSaving}
            data-testid={`notif-pref-new-email-${pref.key}`}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={addEmail}
            disabled={isSaving || !newEmail.trim()}
            data-testid={`notif-pref-add-${pref.key}`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
          </Button>
        </div>

        <div className="flex items-center justify-between pt-2 mt-2 border-t border-zinc-200">
          <span className="text-[10px] text-zinc-500">
            {pref.updated_at && `Modifié le ${new Date(pref.updated_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}`}
            {pref.updated_by && ` par ${pref.updated_by}`}
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onTest(pref.key)}
            disabled={isTesting || !enabled || recipients.length === 0}
            className="text-xs h-7"
            data-testid={`notif-pref-test-${pref.key}`}
            title={!enabled ? 'Activez la notification pour pouvoir tester' : 'Envoyer un email de test'}
          >
            {isTesting
              ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              : <Send className="w-3 h-3 mr-1" />}
            Tester
          </Button>
        </div>
      </div>
    </div>
  );
};

const NotificationPreferencesTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [testingKey, setTestingKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/notification-preferences`, { headers: auth() });
      setItems(r.data.items || []);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erreur de chargement');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = useCallback(async (key, body) => {
    setSavingKey(key);
    try {
      const r = await axios.put(
        `${API}/admin/notification-preferences/${key}`,
        body,
        { headers: auth() },
      );
      setItems((arr) => arr.map((p) => (p.key === key ? r.data : p)));
      toast.success('Préférence enregistrée');
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erreur d\'enregistrement');
      // Recharge pour resynchroniser en cas d'échec
      load();
    } finally {
      setSavingKey(null);
    }
  }, [load]);

  const handleTest = useCallback(async (key) => {
    setTestingKey(key);
    try {
      const r = await axios.post(
        `${API}/admin/notification-preferences/${key}/test`,
        {},
        { headers: auth() },
      );
      toast.success(`Email de test envoyé à ${r.data.recipients.join(', ')}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Erreur lors de l\'envoi de test');
    } finally {
      setTestingKey(null);
    }
  }, []);

  // Group items by category
  const grouped = useMemo(() => {
    const g = {};
    for (const p of items) {
      const cat = p.category || 'autre';
      if (!g[cat]) g[cat] = [];
      g[cat].push(p);
    }
    return g;
  }, [items]);

  const enabledCount = items.filter((p) => p.enabled).length;

  return (
    <Card data-testid="notification-prefs-tab">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
              <Bell className="w-5 h-5 text-emerald-600" /> Préférences notifications
            </h2>
            <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
              Choisissez quels emails envoyer automatiquement et à qui. Toute modification est
              enregistrée immédiatement. Le bouton « Tester » envoie un email vide à la liste pour
              vérifier la chaîne Resend.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-emerald-200 text-emerald-700">
              {enabledCount}/{items.length} actives
            </Badge>
            <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="notif-prefs-refresh">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>
        </div>

        {loading && items.length === 0 ? (
          <div className="text-center py-12 text-zinc-400">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-zinc-500">
            <BellOff className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
            <p>Aucune préférence configurée.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {Object.entries(grouped).map(([cat, prefs]) => (
              <section key={cat} data-testid={`notif-pref-section-${cat}`}>
                <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">
                  {CATEGORY_LABELS[cat] || cat}
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {prefs.map((p) => (
                    <PreferenceCard
                      key={p.key}
                      pref={p}
                      onUpdate={handleUpdate}
                      onTest={handleTest}
                      savingKey={savingKey}
                      testingKey={testingKey}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        <div className="mt-6 p-3 bg-zinc-50 border border-zinc-200 rounded-lg">
          <p className="text-xs text-zinc-600 leading-relaxed">
            <Save className="w-3.5 h-3.5 inline mr-1 text-zinc-400" />
            <strong>Astuce</strong> : pour ne plus recevoir les emails techniques (import Mediciel,
            rapports internes), désactivez la catégorie correspondante. Vous conserverez uniquement
            les notifications liées aux demandes des clients.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificationPreferencesTab;
